import {
  OpenAnalystSandboxError,
  type OpenAnalystSandboxClient,
  type OpenAnalystSandboxReady,
  type OpenAnalystSandboxState,
} from "./openanalyst-sandbox"

export type OpenAnalystRuntimeState =
  | { state: "idle" }
  | { state: "provisioning"; progress?: number; progressMessage?: string }
  | { state: "ready"; connectUrl: string; routeTokenExpiresAt: string }
  | { state: "stopped" }
  | { state: "error"; message: string }

type RuntimeSessionInput<TimerHandle> = {
  client: Pick<OpenAnalystSandboxClient, "provision" | "status" | "reconnect" | "heartbeat" | "stop">
  onState?: (state: OpenAnalystRuntimeState) => void
  now?: () => number
  setTimeout?: (handler: () => void | Promise<void>, timeout?: number) => TimerHandle
  clearTimeout?: (timer: TimerHandle) => void
  heartbeatIntervalMs?: number
  pollIntervalMs?: number
  reconnectBufferMs?: number
}

const DEFAULT_HEARTBEAT_INTERVAL_MS = 30_000
const DEFAULT_POLL_INTERVAL_MS = 2_000
const DEFAULT_RECONNECT_BUFFER_MS = 60_000
const MAX_TIMER_DELAY_MS = 2_147_483_647

function message(error: unknown, fallback: string) {
  console.error("[openanalyst-runtime] session operation failed", error)
  return fallback
}

function ready(data: OpenAnalystSandboxState): data is OpenAnalystSandboxReady {
  return data.state === "ready" || data.status === "ready"
}

function provisioning(data: OpenAnalystSandboxState) {
  return data.state === "provisioning" || data.status === "provisioning" || data.status === "pending"
}

export function createOpenAnalystRuntimeSession<TimerHandle = ReturnType<typeof setTimeout>>(
  input: RuntimeSessionInput<TimerHandle>,
) {
  const now = input.now ?? Date.now
  const setTimer = input.setTimeout ?? ((handler, timeout) => setTimeout(() => void handler(), timeout) as TimerHandle)
  const clearTimer = input.clearTimeout ?? ((timer: TimerHandle) => clearTimeout(timer as ReturnType<typeof setTimeout>))
  const heartbeatIntervalMs = input.heartbeatIntervalMs ?? DEFAULT_HEARTBEAT_INTERVAL_MS
  const pollIntervalMs = input.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS
  const reconnectBufferMs = input.reconnectBufferMs ?? DEFAULT_RECONNECT_BUFFER_MS

  let current: OpenAnalystRuntimeState = { state: "idle" }
  let heartbeatTimer: TimerHandle | undefined
  let pollTimer: TimerHandle | undefined
  let reconnectTimer: TimerHandle | undefined
  let active = true

  const setState = (state: OpenAnalystRuntimeState) => {
    current = state
    input.onState?.(state)
    return state
  }

  const clearHeartbeat = () => {
    if (!heartbeatTimer) return
    clearTimer(heartbeatTimer)
    heartbeatTimer = undefined
  }

  const clearPoll = () => {
    if (!pollTimer) return
    clearTimer(pollTimer)
    pollTimer = undefined
  }

  const clearReconnect = () => {
    if (!reconnectTimer) return
    clearTimer(reconnectTimer)
    reconnectTimer = undefined
  }

  const clearTimers = () => {
    clearHeartbeat()
    clearPoll()
    clearReconnect()
  }

  const applyReady = (data: OpenAnalystSandboxReady) => {
    clearTimers()
    const state = setState({
      state: "ready",
      connectUrl: data.connectUrl,
      routeTokenExpiresAt: data.routeTokenExpiresAt,
    })
    scheduleHeartbeat()
    scheduleReconnect(data.routeTokenExpiresAt)
    return state
  }

  const applyProvisioning = (data: OpenAnalystSandboxState, progressMessage = "Provisioning OpenAnalyst hosted runtime") => {
    const state = setState({
      state: "provisioning",
      progress: typeof data.progress === "number" ? data.progress : undefined,
      progressMessage:
        typeof data.progressMessage === "string" && data.progressMessage.trim()
          ? data.progressMessage
          : progressMessage,
    })
    schedulePoll()
    return state
  }

  const applySandboxState = (data: OpenAnalystSandboxState, progressMessage?: string) => {
    if (ready(data)) return applyReady(data)
    return applyProvisioning(data, progressMessage)
  }

  const provision = async () => {
    setState({ state: "provisioning", progressMessage: "Provisioning OpenAnalyst hosted runtime" })
    try {
      return applySandboxState(await input.client.provision())
    } catch (error) {
      return setState({ state: "error", message: message(error, "OpenAnalyst hosted runtime could not be prepared. Try again.") })
    }
  }

  async function pollStatus() {
    if (!active) return current
    try {
      const status = await input.client.status()
      if (ready(status)) return applyReady(status)
      if (provisioning(status)) return applyProvisioning(status)
      return provision()
    } catch (error) {
      if (error instanceof OpenAnalystSandboxError && error.status === 404) return provision()
      return setState({ state: "error", message: message(error, "OpenAnalyst hosted runtime status could not be checked. Try again.") })
    }
  }

  function schedulePoll() {
    clearPoll()
    if (!active) return
    pollTimer = setTimer(() => {
      void pollStatus()
    }, pollIntervalMs)
  }

  async function runHeartbeat() {
    if (!active || current.state !== "ready") return
    try {
      const result = await input.client.heartbeat()
      if (ready(result)) {
        applyReady(result)
        return
      }
    } catch {
      // Heartbeat should not evict an otherwise usable iframe on transient network failures.
    }
    scheduleHeartbeat()
  }

  function scheduleHeartbeat() {
    clearHeartbeat()
    if (!active || current.state !== "ready") return
    heartbeatTimer = setTimer(() => runHeartbeat(), heartbeatIntervalMs)
  }

  function scheduleReconnect(routeTokenExpiresAt: string) {
    clearReconnect()
    if (!active) return
    reconnectTimer = setTimer(
      () => {
        void reconnect()
      },
      Math.min(MAX_TIMER_DELAY_MS, Math.max(0, Date.parse(routeTokenExpiresAt) - now() - reconnectBufferMs)),
    )
  }

  const start = async () => {
    active = true
    clearTimers()
    setState({ state: "provisioning", progressMessage: "Connecting to OpenAnalyst workspace" })
    try {
      const status = await input.client.status()
      if (ready(status)) return applyReady(status)
      if (provisioning(status)) return applyProvisioning(status)
      return provision()
    } catch (error) {
      if (error instanceof OpenAnalystSandboxError && error.status === 404) return provision()
      return setState({ state: "error", message: message(error, "OpenAnalyst hosted runtime could not connect. Try again.") })
    }
  }

  const reconnect = async () => {
    if (!active) return current
    setState({ state: "provisioning", progressMessage: "Reconnecting OpenAnalyst hosted runtime" })
    try {
      return applySandboxState(await input.client.reconnect(), "Reconnecting OpenAnalyst hosted runtime")
    } catch (error) {
      if (error instanceof OpenAnalystSandboxError && error.status === 404) return provision()
      return setState({ state: "error", message: message(error, "OpenAnalyst hosted runtime could not reconnect. Try again.") })
    }
  }

  const stop = async () => {
    active = false
    clearTimers()
    try {
      await input.client.stop()
      return setState({ state: "stopped" })
    } catch (error) {
      return setState({ state: "error", message: message(error, "OpenAnalyst hosted runtime could not stop. Try again.") })
    }
  }

  return {
    state: () => current,
    start,
    reconnect,
    stop,
    dispose() {
      active = false
      clearTimers()
    },
  }
}
